import { ITransformItem, TransformType } from "@kuika/kuika-cl-model";
import React, { useMemo } from "react";
import KuikaSlider from "../../../../ui-kit/kuika-slider/kuika-slider";
import KuikaTabs, { KuikaTabOption } from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import ScaleLockBracket from "./scale-lock-bracket";
import { AXIS_CONFIG_BY_TYPE, AxisConfig, defaultsForType } from "./transform-defaults";

export interface TransformFormProps {
  item: ITransformItem;
  onChange: (next: ITransformItem) => void;
  disabled?: boolean;
  dataTestId?: string;
}

const TYPE_TAB_OPTIONS: readonly KuikaTabOption[] = [
  { value: "move", label: "Move" },
  { value: "scale", label: "Scale" },
  { value: "rotate", label: "Rotate" },
  { value: "skew", label: "Skew" }
];

const TransformForm: React.FC<TransformFormProps> = ({ item, onChange, disabled, dataTestId }) => {
  const cfg: AxisConfig = AXIS_CONFIG_BY_TYPE[item.type];
  const showZ: boolean = item.type !== "skew";
  const isScale: boolean = item.type === "scale";
  const locked: boolean = isScale && item.uniformScale === true;
  const typeTabOptions: KuikaTabOption[] = useMemo(
    (): KuikaTabOption[] =>
      TYPE_TAB_OPTIONS.map((option: KuikaTabOption): KuikaTabOption => ({
        ...option,
        dataTestId: dataTestId ? `${dataTestId}-type-${option.value}` : undefined
      })),
    [dataTestId]
  );

  const handleTypeChange = (nextType: string): void => {
    const typed: TransformType = nextType as TransformType;
    if (typed === item.type) return;
    onChange({ id: item.id, type: typed, ...defaultsForType(typed) });
  };

  const handleAxisChange = (axis: "x" | "y" | "z", nextValue: number): void => {
    if (locked && (axis === "x" || axis === "y")) {
      // Lock broadcast: the changed axis drives the partner axis.
      onChange({ ...item, x: nextValue, y: nextValue });
      return;
    }
    onChange({ ...item, [axis]: nextValue });
  };

  const handleLockToggle = (): void => {
    onChange({ ...item, uniformScale: !locked });
  };

  return (
    <div className="transform-form" data-testid={dataTestId}>
      <KuikaTabs
        options={typeTabOptions}
        value={item.type}
        onChange={handleTypeChange}
        size="sm"
        disabled={disabled}
        className="transform-form__type-tabs"
        dataTestId={dataTestId ? `${dataTestId}-type-tabs` : undefined}
      />

      <div className="transform-form__axes">
        <div className="transform-form__axis-row">
          <span className="transform-form__axis-label">X</span>
          <KuikaSlider
            value={item.x ?? cfg.defaultValue}
            onChange={(next: number): void => handleAxisChange("x", next)}
            min={cfg.min}
            max={cfg.max}
            step={cfg.step}
            precision={cfg.precision}
            unit={cfg.unit}
            disabled={disabled}
            size="sm"
            dataTestId={dataTestId ? `${dataTestId}-x` : undefined}
          />
          {isScale && (
            <ScaleLockBracket
              locked={locked}
              onToggle={handleLockToggle}
              disabled={disabled}
              dataTestId={dataTestId ? `${dataTestId}-lock` : undefined}
            />
          )}
        </div>

        <div className="transform-form__axis-row">
          <span className="transform-form__axis-label">Y</span>
          <KuikaSlider
            value={item.y ?? cfg.defaultValue}
            onChange={(next: number): void => handleAxisChange("y", next)}
            min={cfg.min}
            max={cfg.max}
            step={cfg.step}
            precision={cfg.precision}
            unit={cfg.unit}
            disabled={disabled}
            size="sm"
            dataTestId={dataTestId ? `${dataTestId}-y` : undefined}
          />
        </div>

        {showZ && (
          <div className="transform-form__axis-row">
            <span className="transform-form__axis-label">Z</span>
            <KuikaSlider
              value={item.z ?? cfg.defaultValue}
              onChange={(next: number): void => handleAxisChange("z", next)}
              min={cfg.min}
              max={cfg.max}
              step={cfg.step}
              precision={cfg.precision}
              unit={cfg.unit}
              disabled={disabled}
              size="sm"
              dataTestId={dataTestId ? `${dataTestId}-z` : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransformForm;
export { AXIS_CONFIG_BY_TYPE, defaultsForType };
export type { AxisConfig };
